import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useStaffDetail, useStaffMutations } from '../../hooks/useStaff.js';
import { ApiError } from '../../lib/api.js';
import { fullName, initials } from '../../lib/names.js';
import { PhotoUpload } from '../../components/common/PhotoUpload.jsx';
import { ConfirmDialog } from '../../components/common/ConfirmDialog.jsx';
import { StatusPill } from '../../components/common/StatusPill.jsx';
import { StaffCardExport } from '../../components/dashboard/StaffCardExport.jsx';

/**
 * StaffEditor — create and edit staff.
 *
 * Routing:
 *   /dashboard/staff/new    → create mode, `id` is undefined
 *   /dashboard/staff/:id    → edit mode, fetches existing record
 *
 * Edit mode flow:
 *   1. Load existing record via useStaffDetail
 *   2. Populate form state
 *   3. User edits → track dirty state
 *   4. Save → PUT the full form state, get updated record back
 *   5. On success: navigate back to /dashboard/staff
 *
 * Create mode flow:
 *   1. Start with empty form
 *   2. Save → POST, get created record with id + slug
 *   3. Navigate to /dashboard/staff/:id (the newly-created one)
 *      so photo upload has an id to attach to
 *
 * Photo upload: only available after the record exists. In create mode,
 * we show the photo uploader but disabled until the staff has been
 * created — attempting to upload before save explains this with a hint.
 *
 * Status transitions:
 *   - If admin changes status to 'left' without specifying left_date,
 *     we pre-fill today's date in the date field but allow them to edit it.
 *   - Backend Patch 3 may or may not auto-populate server-side — either way
 *     we send the date explicitly so we don't depend on that detail.
 */

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  role: '',
  status: 'active',
  hired_date: '',
  left_date: '',
  notes: '',
};

export function StaffEditor() {
  const { id } = useParams();
  const isCreate = !id || id === 'new';
  const navigate = useNavigate();

  const { staff, loading, error: loadError, refresh } = useStaffDetail(isCreate ? null : id);
  const { createStaff, updateStaff, deleteStaff, uploadPhoto, deletePhoto } = useStaffMutations();

  const [form, setForm] = useState(EMPTY_FORM);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Hydrate form when the staff record loads
  useEffect(() => {
    if (staff && !isCreate) {
      setForm({
        first_name: staff.first_name || '',
        last_name: staff.last_name || '',
        role: staff.role || '',
        status: staff.status || 'active',
        hired_date: staff.hired_date || '',
        left_date: staff.left_date || '',
        notes: staff.notes || '',
      });
      setDirty(false);
    }
  }, [staff, isCreate]);

  // Warn on unsaved changes if the user tries to close the tab
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  function setField(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setDirty(true);
    if (fieldErrors[field]) {
      setFieldErrors(fe => ({ ...fe, [field]: null }));
    }

    // Status → left: pre-fill left_date with today if empty
    if (field === 'status' && value === 'left') {
      setForm(f => ({
        ...f,
        status: 'left',
        left_date: f.left_date || new Date().toISOString().slice(0, 10),
      }));
    }
    // Status → not-left: clear left_date so we don't submit a stale one
    if (field === 'status' && value !== 'left' && form.status === 'left') {
      setForm(f => ({ ...f, status: value, left_date: '' }));
    }
  }

  function validate() {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = 'First name is required';
    if (!form.last_name.trim()) errs.last_name = 'Last name is required';
    if (form.status === 'left' && !form.left_date) {
      errs.left_date = 'Add the date they left';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaveError(null);
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        role: form.role.trim() || null,
        status: form.status,
        hired_date: form.hired_date || null,
        left_date: form.status === 'left' ? (form.left_date || null) : null,
        notes: form.notes.trim() || null,
      };

      if (isCreate) {
        const created = await createStaff(payload);
        // Navigate to the newly-created staff so user can upload a photo
        navigate(`/dashboard/staff/${created.id}`, { replace: true });
      } else {
        await updateStaff(id, payload);
        setDirty(false);
        // Stay on the edit page — feedback that save worked
        refresh();
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setSaveError(err.message);
      } else {
        setSaveError('Could not save. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (isCreate) return;
    setDeleting(true);
    try {
      await deleteStaff(id);
      navigate('/dashboard/staff', { replace: true });
    } catch (err) {
      setSaveError(err?.message || 'Could not delete. Please try again.');
      setDeleting(false);
      setDeleteOpen(false);
    }
  }

  async function handlePhotoUpload(file) {
    if (isCreate) return;
    await uploadPhoto(id, file);
    refresh();
  }

  async function handlePhotoDelete() {
    if (isCreate) return;
    await deletePhoto(id);
    refresh();
  }

  if (!isCreate && loading) {
    return <LoadingShell />;
  }
  if (!isCreate && loadError) {
    return <ErrorShell error={loadError} />;
  }

  const staffForInitials = isCreate
    ? { first_name: form.first_name, last_name: form.last_name }
    : staff;

  return (
    <>
      <div className="page-header">
        <div className="page-title-wrap">
          <p className="eyebrow" style={{ marginBottom: 'var(--sp-2)' }}>
            <Link to="/dashboard/staff" style={{ color: 'inherit' }}>← All staff</Link>
          </p>
          <h1 className="page-title">
            {isCreate ? 'Add staff member' : fullName(staff) || 'Edit staff'}
          </h1>
          {!isCreate && staff && (
            <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', marginTop: 'var(--sp-2)', flexWrap: 'wrap' }}>
              <StatusPill status={staff.status} />
              <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                Staff slug: <code style={{ fontFamily: 'var(--font-mono)', background: 'var(--cream)', padding: '2px 6px', borderRadius: 4 }}>{staff.slug}</code>
                {' '}· QR preview coming soon
              </span>
            </div>
          )}
        </div>
      </div>

      {saveError && <div className="alert alert-error" role="alert">{saveError}</div>}

      <form onSubmit={handleSave} className="editor-card" noValidate>
        <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
          <legend className="editor-section-title">Photo</legend>
          <PhotoUpload
            photoUrl={!isCreate ? staff?.photo_url : null}
            initials={initials(staffForInitials)}
            disabled={isCreate}
            onUpload={!isCreate ? handlePhotoUpload : undefined}
            onDelete={!isCreate ? handlePhotoDelete : undefined}
          />
          {isCreate && (
            <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-3)' }}>
              Save the staff member first, then upload a photo.
            </p>
          )}
        </fieldset>

        <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
          <legend className="editor-section-title">Basics</legend>

          <div className="field-row">
            <TextField
              label="First name"
              name="first_name"
              value={form.first_name}
              onChange={v => setField('first_name', v)}
              error={fieldErrors.first_name}
              autoComplete="given-name"
              required
            />
            <TextField
              label="Last name"
              name="last_name"
              value={form.last_name}
              onChange={v => setField('last_name', v)}
              error={fieldErrors.last_name}
              autoComplete="family-name"
              required
            />
          </div>

          <TextField
            label="Role"
            hint="E.g. Senior Cleaner, Support Worker, Site Supervisor"
            name="role"
            value={form.role}
            onChange={v => setField('role', v)}
          />
        </fieldset>

        <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0, marginBottom: 'var(--sp-6)' }}>
          <legend className="editor-section-title">Employment</legend>

          <div className="field-row">
            <div className="field">
              <label className="field-label" htmlFor="f-status">Status</label>
              <select
                id="f-status"
                className="field-select"
                value={form.status}
                onChange={e => setField('status', e.target.value)}
              >
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="left">Left</option>
              </select>
              <p className="field-hint">
                {form.status === 'active' && 'Shown as verified when their QR code is scanned.'}
                {form.status === 'suspended' && 'Shown as under review when scanned — temporary pause.'}
                {form.status === 'left' && 'Shown as no longer employed when scanned.'}
              </p>
            </div>

            <DateField
              label="Hired"
              name="hired_date"
              value={form.hired_date}
              onChange={v => setField('hired_date', v)}
              error={fieldErrors.hired_date}
            />
          </div>

          {form.status === 'left' && (
            <DateField
              label="Left on"
              hint="The date they stopped working for you."
              name="left_date"
              value={form.left_date}
              onChange={v => setField('left_date', v)}
              error={fieldErrors.left_date}
              required
            />
          )}
        </fieldset>

        <fieldset className="editor-section" style={{ border: 0, padding: 0, margin: 0 }}>
          <legend className="editor-section-title">Private notes</legend>
          <div className="field">
            <textarea
              className="field-textarea"
              placeholder="Internal notes. Never shown on the public verify page."
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              maxLength={2000}
            />
            <p className="field-hint">{form.notes.length}/2000 · Private to your team.</p>
          </div>
        </fieldset>

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginTop: 'var(--sp-6)',
          paddingTop: 'var(--sp-5)',
          borderTop: '1px solid var(--line-soft)',
          gap: 'var(--sp-3)',
          flexWrap: 'wrap',
        }}>
          {!isCreate ? (
            <button
              type="button"
              className="btn btn-ghost"
              style={{ color: 'var(--id-red-d)' }}
              onClick={() => setDeleteOpen(true)}
              disabled={saving}
            >
              Delete staff member
            </button>
          ) : (
            <span />
          )}

          <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
            <Link to="/dashboard/staff" className="btn btn-secondary">
              Cancel
            </Link>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || (!isCreate && !dirty)}
              aria-busy={saving}
            >
              {saving
                ? (<><span className="spinner" aria-hidden="true" /> Saving…</>)
                : isCreate ? 'Create staff member' : 'Save changes'}
            </button>
          </div>
        </div>
      </form>

      {!isCreate && staff && (
        <StaffCardExport staff={staff} />
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete this staff member?"
        body={
          <>
            <strong>{fullName(staff)}</strong> will be removed permanently.
            Their scan history is kept for audit but their QR code will
            start returning "unknown" if scanned from this moment on.
          </>
        }
        confirmLabel="Delete permanently"
        destructive
        submitting={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
      />
    </>
  );
}

/* ─────────────────────────────────────────────────────────── */
/*  Small internals                                             */
/* ─────────────────────────────────────────────────────────── */

function TextField({ label, hint, name, value, onChange, error, required, ...rest }) {
  const id = `f-${name}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}{required && <span style={{ color: 'var(--id-red)', marginLeft: 2 }} aria-hidden="true">*</span>}
      </label>
      {hint && <div className="field-hint" id={hintId}>{hint}</div>}
      <input
        id={id}
        className="field-input"
        type="text"
        name={name}
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        required={required}
        {...rest}
      />
      {error && <div className="field-error" id={errorId} role="alert">{error}</div>}
    </div>
  );
}

function DateField({ label, hint, name, value, onChange, error, required }) {
  const id = `f-${name}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}{required && <span style={{ color: 'var(--id-red)', marginLeft: 2 }} aria-hidden="true">*</span>}
      </label>
      {hint && <div className="field-hint" id={hintId}>{hint}</div>}
      <input
        id={id}
        className="field-input"
        type="date"
        name={name}
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
      />
      {error && <div className="field-error" id={errorId} role="alert">{error}</div>}
    </div>
  );
}

function LoadingShell() {
  return (
    <div style={{ padding: 'var(--sp-10) 0', textAlign: 'center' }}>
      <div className="spinner" aria-hidden="true" />
      <p className="muted" style={{ marginTop: 'var(--sp-3)' }}>Loading…</p>
    </div>
  );
}

function ErrorShell({ error }) {
  return (
    <div className="alert alert-error">
      <strong>Couldn't load this staff member.</strong>{' '}
      {error instanceof ApiError && error.status === 404
        ? <>This record doesn't exist. <Link to="/dashboard/staff">Back to staff</Link>.</>
        : <>Check your connection and refresh the page.</>}
    </div>
  );
}
